import React, { useEffect, useRef, useState } from 'react';
import {
  applyConsentDefault, getConsent, grantGoogleConsent, setConsent,
} from '@/lib/consent';
import { LEGAL_LINKS } from '@/lib/legalLinks';

/**
 * Consent-gated third-party tracking for PUBLIC landing pages.
 *
 * Ported from Imprint's _tracking.html. The order of operations is the whole
 * point, and it's what makes shipping marketing pixels defensible under
 * POPIA/GDPR:
 *
 *   1. Google Consent Mode v2 defaults to DENIED before anything else exists.
 *   2. NO third-party script loads until the visitor explicitly accepts.
 *      "Declined" and "no choice yet" both load nothing.
 *   3. Tags are built from OUR code, driven only by validated IDs from the
 *      allowlisted catalogue (shared/tracking_tags.py). There is no path for
 *      pasted script — the author only ever supplied an ID.
 *
 * The same banner also gates the site-wide GTM container (useGTM), which
 * listens for the choice made here. A page with no page tags and GTM off
 * renders nothing at all: no banner, no dataLayer, zero tracker bytes. The
 * banner appears only when something would load AND the visitor hasn't
 * chosen yet.
 *
 * Never render this in the editor preview — it would fire real pixels on the
 * author's own view and pollute the numbers.
 */

function loadScript(src) {
  const el = document.createElement('script');
  el.async = true;
  el.src = src;
  document.head.appendChild(el);
}

// Each loader mirrors the vendor's own snippet, keyed on a validated ID.
function loadTrackers(tags) {
  const w = window;
  w.dataLayer = w.dataLayer || [];
  w.gtag = w.gtag || function () { w.dataLayer.push(arguments); };

  // GA4 + Google Ads share one gtag.js load.
  const gids = tags.filter((t) => t.type === 'ga4' || t.type === 'google_ads').map((t) => t.id);
  if (gids.length) {
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gids[0])}`);
    gids.forEach((id) => w.gtag('config', id));
  }

  tags.forEach((t) => {
    if (t.type === 'meta_pixel') {
      if (!w.fbq) {
        const n = (w.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
        loadScript('https://connect.facebook.net/en_US/fbevents.js');
      }
      w.fbq('init', t.id);
      w.fbq('track', 'PageView');
    } else if (t.type === 'linkedin_insight') {
      w._linkedin_partner_id = t.id;
      w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
      w._linkedin_data_partner_ids.push(t.id);
      loadScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
    } else if (t.type === 'microsoft_uet') {
      const s = document.createElement('script');
      s.src = 'https://bat.bing.com/bat.js';
      s.async = true;
      s.onload = function () {
        // eslint-disable-next-line no-undef
        w.uetq = new UET({ ti: t.id, enableAutoSpaTracking: true, q: w.uetq });
        w.uetq.push('pageLoad');
      };
      document.head.appendChild(s);
      w.uetq = w.uetq || [];
    } else if (t.type === 'tiktok_pixel') {
      w.TiktokAnalyticsObject = 'ttq';
      const ttq = (w.ttq = w.ttq || []);
      ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
        'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
      ttq.setAndDefer = function (o, m) {
        o[m] = function () { o.push([m].concat(Array.prototype.slice.call(arguments, 0))); };
      };
      ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
      loadScript(`https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(t.id)}&lib=ttq`);
      ttq.page();
    } else if (t.type === 'x_pixel') {
      if (!w.twq) {
        const tw = (w.twq = function () {
          tw.exe ? tw.exe.apply(tw, arguments) : tw.queue.push(arguments);
        });
        tw.version = '1.1'; tw.queue = [];
        loadScript('https://static.ads-twitter.com/uwt.js');
      }
      w.twq('config', t.id);
    }
  });
}

export default function TrackingScripts({ tags, gtmEnabled = false }) {
  const [showBanner, setShowBanner] = useState(false);
  const loaded = useRef(false);
  const clean = (tags || []).filter((t) => t && t.type && t.id);
  const needed = clean.length > 0 || gtmEnabled;

  const loadPageTags = () => {
    if (loaded.current || !clean.length) return;
    loaded.current = true;
    loadTrackers(clean);
  };

  useEffect(() => {
    if (!needed) return;
    // Consent Mode v2 default: DENIED, before any tracker exists.
    applyConsentDefault();
    const choice = getConsent();
    if (choice === 'granted') {
      grantGoogleConsent();
      loadPageTags();
    } else if (choice !== 'denied') {
      setShowBanner(true);   // no choice yet — ask, load nothing
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needed, tags]);

  const choose = (granted) => {
    setShowBanner(false);
    if (granted) {
      grantGoogleConsent();
      loadPageTags();
    }
    setConsent(granted);   // useGTM loads the container on 'granted'
  };

  if (!showBanner) return null;

  return (
    <div
      data-testid="consent-banner"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 90,
        background: '#0A1410', color: '#fff', padding: '14px 20px',
        display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'center',
        flexWrap: 'wrap', fontSize: '.9rem',
        fontFamily: "'Montserrat', Arial, sans-serif",
      }}
    >
      <span style={{ maxWidth: 560 }}>
        We&rsquo;d like to use cookies for analytics and campaign measurement on this page.
        Nothing is set before you choose. See our{' '}
        <a
          href={LEGAL_LINKS.cookies.href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#fff', textDecoration: 'underline' }}
          data-testid="consent-cookie-policy"
        >
          Cookie Policy
        </a>
        .
      </span>
      <button
        type="button"
        data-testid="consent-accept"
        onClick={() => choose(true)}
        style={{
          background: '#144733', border: '1px solid rgba(255,255,255,.25)', color: '#fff',
          padding: '10px 20px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
        }}
      >
        Accept
      </button>
      <button
        type="button"
        data-testid="consent-decline"
        onClick={() => choose(false)}
        style={{
          background: 'none', border: '1px solid #5c6b61', color: '#fff',
          padding: '10px 20px', borderRadius: 6, cursor: 'pointer',
        }}
      >
        Decline
      </button>
    </div>
  );
}
